import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Input,
  Form,
  Switch,
  Select,
  Popconfirm,
  Button,
  Modal,
  Alert,
  Table,
  Tag,
  message,
  Tooltip,
  Space,
  Upload,
  DatePicker,
  Tabs,
  Checkbox,
  InputNumber,
  Badge,
} from 'antd';
import { PageHeader } from '../../components/page-headers/page-headers';
import axios from '../../backendServiceWithoutTimeout';
import { GlobalUtilityStyle, PaginationStyle } from '../../container/styled';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { BiEditAlt } from 'react-icons/bi';
import { FaEye } from 'react-icons/fa';
import EditPlanModal from './EditPlanModal';
import ViewPlanModal from './ViewPlanModal';
import { MdDelete } from 'react-icons/md';

const { TextArea } = Input;

function Plans() {
  const [form] = Form.useForm();
  const [isVisible, setIsVisible] = useState(false);
  const [isEditVisible, setIsEditVisible] = useState(false);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [imageURL, setImageURL] = useState(null);
  const [updatedImageURL, setUpdatedImageURL] = useState(null);
  const [courseData, setCourseData] = useState([]);
  const [courseId, setCourseId] = useState(null);
  const [batchData, setBatchData] = useState([]);
  const [planData, setPlanData] = useState([]);
  const [courseLoading, setCourseLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchesToShow, setBatchesToShow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [featuresState, setFeaturesState] = useState({});
  const [featuresEditState, setEditFeaturesState] = useState({});
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentViewPlan, setCurrentViewPlan] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [currentCourseId, setCurrentCourseId] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [planToDlt, setPlanToDlt] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const fetchData = async (page = 1, pageSize = 10, name = '') => {
    try {
      setCourseLoading(true);
      setBatchLoading(true);

      // Fetch paginated plans
      const response = await axios.get('/plan/paginatedPlans', {
        params: { page, pageSize, name },
      });

      const { plans, totalRecords } = response.data.data;

      // Transform the plans data for the table
      const transformedPlans = plans.map((plan) => ({
        ...plan,
        key: plan._id,
        features: plan.features.map((featureType) => ({
          type: featureType.type_name,
          price: featureType.type_price,
          enabledFeatures: featureType.features_to_show
            .filter((f) => f.isEnabled)
            .map((f) => f.feature_name)
            .join(', '),
        })),
        courseName: plan.course?.courseName || 'N/A',
        batchName: plan.batch?.name || 'N/A',
      }));

      setData(transformedPlans);

      // Set pagination data
      setPagination({
        current: page,
        pageSize,
        total: totalRecords,
      });

      // Fetch additional data
      const course_data = await axios.get(`/courses`);
      setCourseData(course_data.data.data);
      setCourseLoading(false);

      const batch_data = await axios.get(`/batch`);
      setBatchData(batch_data.data.allBatches);
      setBatchLoading(false);

      const plan_data = await axios.get(`/plan`);
      setPlanData(plan_data.data.allPlans);

      const features_data = await axios.get(`/features`);
      setFeatures(features_data.data.all_features);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize, searchQuery);
  }, [pagination.current, pagination.pageSize, searchQuery]);
  // Open a modal by clicking new course
  const showModal = () => {
    setIsVisible(true);
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to the first page on search
  };

  // Handle table pagination
  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };
  //Close the modal
  const onCancel = () => {
    setIsVisible(false);
  };

  //Close the Edit modal
  const onEditCancel = () => {
    setCurrentPlan(null);
    setIsEditVisible(false);
  };

  //Close the View modal
  const onViewCancel = () => {
    setOpenViewModal(false);
  };

  // Handle search query input change
  const onSearchQueryChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter the data based on the search query
  const filteredData = data.filter(
    (item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()),
    // item.name.toLowerCase().startsWith(searchQuery.toLowerCase()),
  );

  // Open Edit Modal
  const handleEditModal = (record) => {
    setIsEditVisible(true);
    setCurrentPlan(record);
  };

  // Open View Modal
  const handleViewModal = (record) => {
    setOpenViewModal(true);
    setCurrentViewPlan(record);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Tooltip title={text}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '250px',
              paddingLeft: '5px',
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Course',
      dataIndex: 'course',
      key: 'course',
      render: (text, record) => (
        <div className="flex items-center justify-start gap-1 flex-wrap">
          <Badge
            size={'default'}
            count={text.courseName}
            className="text-xs"
            style={{ background: '#865CD3', fontSize: '12px' }}
          />
        </div>
      ),
    },
    {
      title: 'Batch',
      dataIndex: 'batch',
      key: 'batch',
      render: (text, record) => (
        <div className="flex items-center justify-start gap-1 flex-wrap">
          <Badge
            size={'default'}
            count={text.name}
            className="text-xs"
            style={{ background: '#865CD3', fontSize: '12px' }}
          />
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => (
        <div className="flex">
          <FaEye
            className="mx-2 w-4 h-4 cursor-pointer text-primary dark:text-white60 hover:text-success dark:hover:text-success"
            onClick={() => handleViewModal(record)}
          />
          <BiEditAlt
            className="mx-2 w-4 h-4 cursor-pointer text-primary dark:text-white60 hover:text-success dark:hover:text-success"
            onClick={() => handleEditModal(record)}
          />
          <MdDelete
            className="mx-2 w-4 h-4 cursor-pointer text-primary dark:text-white60 hover:text-success dark:hover:text-success"
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  const handleDelete = async (record) => {
    setPlanToDlt(record._id);
    setOpenDelete(true);
  };

  const closeDeleteModal = () => {
    setOpenDelete(false);
  };

  const handlePlanDelete = async () => {
    try {
      const response = await axios.delete(`/plan/delete-plan/${planToDlt}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const res = response.data;

      if (res.status_code === 200) {
        form.resetFields();
        message.success(res.message);
        fetchData();
        closeDeleteModal();
      } else {
        message.error(res.message);
        fetchData();
        closeDeleteModal();
      }
    } catch (error) {
      console.error('Error:', error.response ? error.response.data : error.message);
      message.error(error.response?.data?.message || error.message);
    }
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);

    // Ensure data is available before checking for duplicates
    if (!data || data.length === 0) {
      message.error('Data is not loaded yet. Please try again.');
      setLoading(false);
      return;
    }

    // Trim the name input and check for duplicates
    const trimmedName = values.name.trim();
    const isDuplicate = data.some((plan) => plan.name.trim().toLowerCase() === trimmedName.toLowerCase());

    if (isDuplicate) {
      message.error('A plan with the same name already exists.');
      setLoading(false);
      return;
    }

    // Proceed with the rest of the form submission
    const formValuesWithFeatures = {
      ...values,
      ...featuresState,
      name: trimmedName, // Use the trimmed name
    };

    const featureTypes = ['FREE', 'SILVER', 'GOLD', 'PLATINUM'];
    const featuresToShow = featureTypes.map((type) => ({
      type_name: type,
      features_to_show: features.map((feature) => ({
        feature_name: feature.name,
        isEnabled: formValuesWithFeatures[`${type.toLowerCase()}_${feature.name}`] || false,
      })),
      type_price: formValuesWithFeatures[`${type.toLowerCase()}_price`]
        ? Number(formValuesWithFeatures[`${type.toLowerCase()}_price`])
        : 0,
      toDisplay: formValuesWithFeatures[`${type.toLocaleLowerCase()}_toDisplay`] || false,
    }));

    const planData = {
      name: trimmedName,
      features: featuresToShow,
      discount_codes: values.options,
      course: values.courseName,
      batch: values.batch,
    };

    try {
      const response = await axios.post(`/plan/add-new-plan`, planData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.data.status_code === 400) {
        message.error(response.data.message);
      } else {
        setLoading(false);
        setCourseId(null);
        fetchData();
        message.success('Plan created');
        resetFeaturesSelection();
        form.resetFields();
        onCancel();
      }
    } catch (err) {
      console.error('Error:', err.response ? err.response.data : err.message);
      setLoading(false);
      message.error('Something went wrong, check if all fields are correct');
    }
  };

  const planTypes = ['FREE', 'SILVER', 'GOLD', 'PLATINUM'];

  useEffect(() => {
    if (imageURL) {
      let urlArr = imageURL.split(`/`);
      const receivedUpdatedImageURL = `https://hls-streaming-classroom.s3.ap-south-1.amazonaws.com/${
        urlArr[urlArr.length - 1]
      }`;
      setImageURL(receivedUpdatedImageURL);
      setUpdatedImageURL(receivedUpdatedImageURL);
    }
  }, [imageURL]);

  const resetFeaturesSelection = () => {
    const initialFeaturesState = {};
    planTypes.forEach((item) => {
      features.forEach((feature) => {
        initialFeaturesState[`${item.toLowerCase()}_${feature.name}`] = false;
      });
    });
    setFeaturesState(initialFeaturesState);
  };

  useEffect(() => {
    setCurrentPlan(null);
    resetFeaturesSelection();
  }, []);

  const handleCheckboxChange = (item, featureName, checked) => {
    setFeaturesState((prevState) => ({
      ...prevState,
      [`${item.toLowerCase()}_${featureName}`]: checked,
    }));

    // If any feature is checked, set the plan's switch to true
    // updateDisplaySwitch(item, true);
  };

  const displaySwitchChange = (item, checked) => {
    setFeaturesState((prevState) => ({
      ...prevState,
      [`${item.toLowerCase()}_toDisplay`]: checked,
    }));
  };

  const updateDisplaySwitch = (item, checked) => {
    setFeaturesState((prevState) => ({
      ...prevState,
      [`${item.toLowerCase()}_toDisplay`]: checked,
    }));
  };

  const initialCurrentPlanFeaturesState = {};
  const initialEditFeaturesState = {};

  useEffect(() => {
    if (currentPlan) {
      currentPlan.features.forEach((item) => {
        item.features_to_show.forEach((feature) => {
          initialCurrentPlanFeaturesState[`${item.type_name.toLowerCase()}_${feature.feature_name}`] =
            feature.isEnabled;
          initialCurrentPlanFeaturesState[`${item.type_name.toLocaleLowerCase()}_price`] = item.type_price;
          initialCurrentPlanFeaturesState[`${item.type_name.toLocaleLowerCase()}_toDisplay`] = item.toDisplay;
        });
      });
      planTypes.forEach((item) => {
        features.forEach((feature) => {
          initialEditFeaturesState[`${item.toLowerCase()}_${feature.name}`] =
            initialCurrentPlanFeaturesState[`${item.toLowerCase()}_${feature.name}`];
          initialEditFeaturesState[`${item.toLocaleLowerCase()}_price`] =
            initialCurrentPlanFeaturesState[`${item.toLocaleLowerCase()}_price`];
          initialEditFeaturesState[`${item.toLocaleLowerCase()}_toDisplay`] =
            initialCurrentPlanFeaturesState[`${item.toLocaleLowerCase()}_toDisplay`];
        });
      });
      // console.log(initialEditFeaturesState,'--------------------------initialEditFeaturesState')
      setEditFeaturesState(initialEditFeaturesState);
      setCurrentCourseId(currentPlan.course._id);
      setCurrentBatchId(currentPlan.batch._id);
    }
  }, [currentPlan]);

  const handleEditFormSubmit = async (values) => {
    setLoading(true);

    // Combine form values with checkbox values
    const formValuesWithFeatures = {
      ...values,
      ...featuresEditState,
    };

    // console.log('xxxxxxxxxxxxxxxxxxx', formValuesWithFeatures);

    const featureTypes = ['FREE', 'SILVER', 'GOLD', 'PLATINUM'];

    // Map form values to features for each feature type
    const featuresToShow = featureTypes.map((type) => ({
      type_name: type,
      features_to_show: features.map((feature) => ({
        feature_name: feature.name,
        isEnabled: formValuesWithFeatures[`${type.toLowerCase()}_${feature.name}`] || false,
      })),
      type_price: formValuesWithFeatures[`${type.toLowerCase()}_price`]
        ? Number(formValuesWithFeatures[`${type.toLowerCase()}_price`])
        : 0,
      toDisplay: formValuesWithFeatures[`${type.toLocaleLowerCase()}_toDisplay`] || false,
    }));

    const data = {
      name: values.name,
      features: featuresToShow,
      discount_codes: values.options,
      course: currentCourseId,
      batch: currentBatchId,
    };

    try {
      // console.log('before editingggggggg', data);
      const response = await axios.put(`/plan/update-plan/${currentPlan._id}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.data.status_code === 400) {
        message.error(response.data.message);
      } else {
        setLoading(false);
        setCurrentPlan(null);
        setCurrentCourseId(null);
        setCurrentBatchId(null);
        fetchData();
        message.success('Plan updated');
        form.resetFields();
        onEditCancel();
      }
    } catch (err) {
      console.error('Error:', err.response ? err.response.data : err.message);
      setLoading(false);
      message.error('Something went wrong, check if all fields are correct');
    }
  };

  // Change batches to display as per course
  useEffect(() => {
    form.setFieldValue('batch', null);
    let batchesWithPlan = [];
    planData.map((item) => {
      return batchesWithPlan.push(item.batch._id);
    });
    // console.log(batchesWithPlan, '------------------batchesWithPlan');
    const courseWiseBatches = batchData.filter((item) => item.course._id === courseId);
    const filteredCourseWiseBatches = courseWiseBatches.filter((item) => {
      return !batchesWithPlan.includes(item._id);
    });
    // console.log(filteredCourseWiseBatches, '------------------filteredCourseWiseBatches');
    setBatchesToShow(filteredCourseWiseBatches);
  }, [courseId]);

  return (
    <>
      <PageHeader
        className="flex items-center justify-between px-8 xl:px-[15px] pt-7 pb-6 sm:pb-[30px] bg-transparent sm:flex-col"
        title=""
      />
      <main className="min-h-[715px] lg:min-h-[580px] px-8 xl:px-[15px] bg-transparent">
        <Row gutter={30}>
          <Col xs={24}>
            <>
              <div className="bg-white dark:bg-white10 m-0 text-theme-gray dark:text-white60 text-[15px] rounded-10 relative p-[25px]">
                <div className="text-dark dark:text-white87 font-medium text-[17px] flex items-center justify-between gap-[15px] sm:justify-left mb-5">
                  <div className="flex items-center justify-between gap-[15px]">
                    <h1 className="mb-0 inline-block text-ellipsis text-[18px] font-semibold sm:text-center">Plans</h1>
                    <Input
                      className="w-[150px]"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder="Search by name..."
                    />
                  </div>
                  <Button
                    onClick={showModal}
                    className="h-[50px] px-6 text-xs font-medium bg-primary-transparent text-primary border-none"
                    transparented
                    type="primary"
                    size="large"
                  >
                    + Add Plan
                  </Button>
                </div>
                {/*Display courses list */}
                <div className="mt-0.5">
                  <>
                    <GlobalUtilityStyle>
                      <PaginationStyle>
                        <div className="table-pl-0 hover-tr-none table-pt-15 table-responsive ant-pagination-custom-style [&>div>div>div>ul]:pt-[30px] [&>div>div>div>ul]:mt-[20px] [&>div>div>div>ul]:border-normal dark:[&>div>div>div>ul]:border-white10 [&>div>div>div>ul]:border-t-1 [&>div>div>div>ul]:justify-center">
                          <Table
                            dataSource={data}
                            columns={columns}
                            rowKey={(record) => record._id}
                            pagination={{
                              current: pagination.current,
                              pageSize: pagination.pageSize,
                              total: pagination.total,
                              onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
                              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            loading={loading}
                          />
                        </div>
                      </PaginationStyle>
                    </GlobalUtilityStyle>
                  </>
                </div>
              </div>

              {/* Add new plan Modal */}
              <Modal
                open={isVisible}
                style={{ top: 20 }}
                width={1000}
                title="Add a new plan"
                okText="Create"
                cancelText="Cancel"
                onCancel={onCancel}
                onOk={() => {
                  form
                    .validateFields()
                    .then((values) => {
                      handleFormSubmit(values);
                    })
                    .catch((info) => {
                      console.log('Validate Failed:', info);
                    });
                }}
              >
                <Form
                  form={form}
                  layout="horizontal"
                  name="form_in_modal"
                  initialValues={{
                    modifier: 'public',
                  }}
                >
                  {/* Enter Name */}
                  <Form.Item
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter name of the plan',
                      },
                    ]}
                  >
                    <Input autoComplete="off" placeholder="Enter the name of plan" />
                  </Form.Item>

                  {/* Enter Description */}
                  {/* <Form.Item
                    name="description"
                    rules={[
                      {
                        required: false,
                        message: 'Please enter description of the plan',
                      },
                    ]}
                  >
                    <TextArea type="textarea" rows={4} cols={4} placeholder="Enter plan description" />
                  </Form.Item> */}

                  {/* Select course */}
                  {/* Select course */}
                  <Form.Item
                    size={'large'}
                    name="courseName"
                    rules={[{ required: true, message: 'Please select a course!' }]}
                  >
                    <Select placeholder="Select course" onChange={(e) => setCourseId(e)}>
                      {courseLoading && courseData.length === 0 ? (
                        <Select.Option value={null}>Loading courses .....</Select.Option>
                      ) : courseData.length === 0 ? (
                        <Select.Option value={null}>No courses assigned.</Select.Option>
                      ) : (
                        courseData
                          .filter((course) => course.isActive) // Show only active courses
                          .map((course) => (
                            <Select.Option key={course._id} value={course._id}>
                              {course.courseName}
                            </Select.Option>
                          ))
                      )}
                    </Select>
                  </Form.Item>

                  {/* Select Batches */}
                  <Form.Item
                    size={'large'}
                    name="batch"
                    rules={[{ required: true, message: 'Please select a batch!' }]}
                  >
                    <Select disabled={batchesToShow.length === 0} placeholder="Select batch">
                      {batchLoading && batchesToShow.length === 0 ? (
                        <Select.Option value={null} disabled />
                      ) : batchesToShow.length === 0 ? (
                        <Select.Option value={null} disabled />
                      ) : (
                        batchesToShow.map((batch) => (
                          <Select.Option key={batch._id} value={batch._id}>
                            {batch.name}
                          </Select.Option>
                        ))
                      )}
                    </Select>
                  </Form.Item>

                  {/* Select Features */}
                  <Row gutter={8}>
                    {planTypes.map((item, index) => (
                      <Col span={12} key={index}>
                        <div span={16} className="border my-2">
                          <div className="flex items-center justify-between w-full flex-wrap px-3 border-b text-lg font-semibold">
                            <div>
                              {item} <span className="text-xs font-normal">(Select features to enable)</span>{' '}
                            </div>
                            <Form.Item name={`${item.toLocaleLowerCase()}_toDisplay`} className="mb-0">
                              <Tooltip title={`Enable to display this plan`}>
                                <Switch
                                  onChange={(checked) => displaySwitchChange(item, checked)}
                                  checked={featuresState[`${item.toLowerCase()}_toDisplay`]}
                                />
                              </Tooltip>
                            </Form.Item>
                          </div>
                          {features.map((feature, i) => (
                            <Form.Item key={i} name={`${item.toLowerCase()}_${feature.name}`} className="p-0 m-0">
                              <div className="flex items-center justify-between mx-3 flex-wrap">
                                <p className="mb-0">{feature.name}</p>
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 shadow-xl"
                                  onChange={(e) => handleCheckboxChange(item, feature.name, e.target.checked)}
                                  checked={featuresState[`${item.toLowerCase()}_${feature.name}`]}
                                />
                              </div>
                            </Form.Item>
                          ))}
                        </div>
                        {/* Enter Price */}
                        <Form.Item
                          label={<p className="mb-0 text-sm">Enter price for {item.toLowerCase()} plan</p>}
                          name={`${item.toLowerCase()}_price`}
                          rules={[
                            {
                              required:
                                featuresState[`${item.toLowerCase()}_toDisplay`] && item.toLocaleLowerCase() !== 'free'
                                  ? true
                                  : false,
                              message: `Please enter price of ${item.toLowerCase()} plan`,
                            },
                          ]}
                        >
                          <Input
                            autoComplete="off"
                            type="number"
                            disabled={item.toLocaleLowerCase() === 'free' ? true : false}
                            className=" capitalize"
                            placeholder={`Enter the price of ${item.toLowerCase()} plan`}
                            size={'middle'}
                          />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>

                  {/* Discount coupons */}
                  {/* <Form.List name="options">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }, index) => (
                          <Row key={index} gutter={8} className="flex items-center justify-start flex-wrap">
                            <Col span={11}>
                              <Form.Item
                                {...restField}
                                name={[name, 'discount_coupon']}
                                rules={[{ required: true, message: 'Please enter coupon code' }]}
                              >
                                <Input autoComplete="off" placeholder="Discount Coupon" />
                              </Form.Item>
                            </Col>
                            <Col span={11}>
                              <Form.Item
                                {...restField}
                                name={[name, 'percentage_discount']}
                                rules={[{ required: true, message: 'Please enter percentage discount value' }]}
                              >
                                <Input autoComplete="off" type="number" placeholder="Percentage discount" />
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} />
                            </Col>
                          </Row>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            disabled={fields.length === 5}
                          >
                            Add Discount Coupons
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List> */}
                </Form>
              </Modal>

              {/* Edit plan Modal */}
              <EditPlanModal
                currentPlan={currentPlan}
                isEditVisible={isEditVisible}
                onEditCancel={onEditCancel}
                courseId={courseId}
                courseData={courseData}
                courseLoading={courseLoading}
                batchesToShow={batchesToShow}
                batchLoading={batchLoading}
                planTypes={planTypes}
                features={features}
                featuresEditState={featuresEditState}
                setEditFeaturesState={setEditFeaturesState}
                handleEditFormSubmit={handleEditFormSubmit}
                setCurrentPlan={setCurrentPlan}
              />

              {/* View plan modal */}
              <ViewPlanModal currentPlan={currentViewPlan} openViewModal={openViewModal} onViewCancel={onViewCancel} />
            </>
          </Col>
        </Row>

        {/* Delete modal */}
        <Modal
          title={
            <div>
              <h1>Are you sure delete this plan?.</h1>
              <span className="text-sm font-normal">
                This will further delete user purchase records who have bought this plan.
              </span>
            </div>
          }
          open={openDelete}
          onCancel={closeDeleteModal}
          footer={null}
        >
          <div className="flex items-center justify-end gap-2">
            <Button onClick={closeDeleteModal} type={'ghost'}>
              Cancel
            </Button>
            <Button onClick={handlePlanDelete} type={'danger'}>
              Delete
            </Button>
          </div>
        </Modal>
      </main>
    </>
  );
}

export default Plans;
